import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { CombinedState } from 'reducers';
import { changeFrameAsync, deleteFrameAsync } from 'actions/annotation-actions';
import { sharedThumbnailLoader } from 'utils/frame-thumbnails';
import { workspacePath } from 'utils/workspace-route';
import { openAddImagesModal } from './add-images-modal';
import { useSaveFrameOrder } from './save-frame-order';
import FrameStrip from './frame-strip';
import { confirmDeleteFrame } from './frame-strip-container';
import type { FrameStatus } from './steps/steps-api';
import { useReferencesContext } from './references/references-context';
import { useStepsContext } from './steps/steps-context';
import { useTaskModes } from './references/use-task-modes';
import { useSaveThenNavigate } from './references/use-save-then-navigate';

const PHOTO_STATUS: Record<FrameStatus, string> = {
    done: 'every detection has a model',
    partial: 'some detections have no model',
    pending: 'no models yet',
    running: '',
    failed: '',
};

// strip numbers are positions across every linked reference, not frame numbers
export default function ReferenceStripContainer(): JSX.Element | null {
    const dispatch = useDispatch();
    const { tid } = useParams<{ tid: string }>();
    const taskId = Number(tid);
    const job = useSelector((state: CombinedState) => state.annotation.job.instance);
    const frame = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    // flips when a photo is deleted or restored: the photo list is fetched again
    const deletedVersion = useSelector((state: CombinedState) => state.annotation.player.frame.data?.deleted);
    const modes = useTaskModes(taskId, deletedVersion);
    const references = useReferencesContext();
    const saveFrameOrder = useSaveFrameOrder();
    // Ctrl+click picks photos for the Steps tab's "on selected"
    const steps = useStepsContext();
    const navigate = useSaveThenNavigate();
    const [urls, setUrls] = useState<Record<number, string>>({});
    const requested = useRef(new Set<number>());
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const photos = modes?.photos ?? [];
    const current = photos.find((photo) => photo.jobId === job?.id && photo.frame === frame)?.index ?? -1;
    const status = useMemo(() => {
        const result: Record<number, FrameStatus> = {};
        for (const photo of photos) {
            const value = photo.jobId === job?.id ? references?.frameStatus[photo.frame] : undefined;
            if (value) result[photo.index] = value;
        }
        return result;
    }, [photos, job?.id, references?.frameStatus]);

    const onVisible = useCallback((index: number) => {
        const photo = photos[index];
        if (!photo || requested.current.has(index)) return;
        requested.current.add(index);
        sharedThumbnailLoader('reference_jobs', photo.jobId).load(photo.frame)
            .then((url) => { if (mounted.current) setUrls((prev) => ({ ...prev, [index]: url })); })
            .catch(() => requested.current.delete(index));
    }, [photos]);

    const onSelect = useCallback((index: number) => {
        const photo = photos[index];
        if (!photo || !job) return;
        if (photo.jobId === job.id) {
            dispatch(changeFrameAsync(photo.frame) as any);
        } else {
            navigate(`${workspacePath(taskId, photo.jobId, photo.referenceId)}?frame=${photo.frame}`);
        }
    }, [photos, job, dispatch, navigate, taskId]);

    if (!job || photos.length === 0) return null;
    const referenceId = photos.find((photo) => photo.jobId === job.id)?.referenceId;

    return (
        <FrameStrip
            frames={photos.map((photo) => ({
                number: photo.index,
                url: urls[photo.index] ?? null,
                name: photo.name,
                deletable: photo.jobId === job.id,
                draggable: photo.jobId === job.id,
            }))}
            current={current}
            onSelect={onSelect}
            onVisible={onVisible}
            status={status}
            noun='Photo'
            statusText={PHOTO_STATUS}
            selected={steps?.selected}
            onToggleSelect={steps?.toggleSelected}
            onAdd={referenceId === undefined ? undefined : () => openAddImagesModal(`/references/${referenceId}/data`, 'photos')}
            // the strip is by position across the references, the server by the frame of this reference
            onReorder={referenceId === undefined ? undefined : (order) => saveFrameOrder(
                `/references/${referenceId}/data`, order.map((index) => photos[index].frame),
            )}
            onDelete={(index) => confirmDeleteFrame('photo', index + 1, () => {
                dispatch(deleteFrameAsync(photos[index].frame) as any);
            })}
        />
    );
}
